import React, { Fragment, useEffect, useMemo, useState } from 'react';
import styles from './question-info-page.module.scss';
import { Language } from '_types/language';
import conversationClient, { FavouriteQuestion } from 'api/conversation-client';
import { Loading } from 'components/loading';
import {
    returnCopyForLanguage,
    returnRoutesForLanguage,
} from 'services/language-service';
import { RouteList, routesEN, routesNO } from 'api/routes';
import { SEO } from 'components/seo';
import { ConversationWithCategory, deckParentPages, Question } from '_types';
import classnames from 'classnames';
import {
    favouriteQuestionsSelector,
    setFavouriteQuestions,
} from 'store-modules/favourite-questions';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchQuery } from 'hooks/use-search-query';
import { Box, Button, Stack } from 'components/base';
import { ReactComponent as MoreVertIcon } from '../../images/icons/more-vert.svg';
import { IS_IOS_APP } from '../../constants';
import { IS_ANDROID_APP } from '../../constants';
import { Image, createCanvas, loadImage } from 'canvas';
import sharingClient from 'api/sharing-client';
import { ToggleConfirmation } from 'components/toggle-confirmation';
import {
    myConversationsSelector,
    setMyConversations,
} from 'store-modules/my-conversations';
import { BackButton } from 'components/back-button';
import { ConversationCard } from 'components/conversation-card';
import classNames from 'classnames';
import Decorator from 'components/conversation-card/decorator';
import { setToastMessage } from 'store-modules/user';

import { useNavigate } from 'react-router-dom';
import useTypedLocation from 'hooks/use-typed-location';

interface LocationState {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchString: any;
    question: Question;
    backPath?: string;
    onRemoveQuestion: (question: Question) => void;
    containerClassName: string;
    viewAllSearchQuestions: boolean;
    conversationSearchResult: ConversationWithCategory[];
    questionSearchResult: Question[];
    parentPage: string;
    viewAllSearchConvos?: boolean;
}

interface Props {
    language: Language;
}

interface ViewCopy {
    addToConversationText: string;
    addToConversationToastText: string;
    addToFavouritesLabel: string;
    createConversationLabel: string;
    deleteQuestionButtonText: string;
    deleteQuestionCancelText: string;
    deleteQuestionTitle: string;
    removeFromFavouritesLabel: string;
    seoTitle: string;
    shareQuestionText: string;
    toFrontPage: string;
    byText: string;
}

const nbCopy: ViewCopy = {
    addToConversationText: 'Legg til i samtale',
    addToConversationToastText: 'Spørsmålet er lagt til i samtalen ',
    addToFavouritesLabel: 'Lik spørsmål',
    createConversationLabel: 'Ny samtale',
    deleteQuestionButtonText: 'Fjern Spørsmål',
    deleteQuestionCancelText: 'Avbryt',
    deleteQuestionTitle: 'Fjern fra likte',
    removeFromFavouritesLabel: 'Liker ikke',
    seoTitle: 'Spørsmål',
    shareQuestionText: 'Del spørsmålet',
    toFrontPage: 'Gå til forsiden',
    byText: 'av ',
};

const enCopy: ViewCopy = {
    addToConversationText: 'Add to conversation',
    addToConversationToastText: 'Question added to the conversation ',
    addToFavouritesLabel: 'Like question',
    createConversationLabel: 'New conversation',
    deleteQuestionButtonText: 'Remove Question',
    deleteQuestionCancelText: 'Cancel',
    deleteQuestionTitle: 'Remove from liked',
    removeFromFavouritesLabel: 'Unlike',
    seoTitle: 'Questions',
    shareQuestionText: 'Share question',
    toFrontPage: 'Go to front page',
    byText: 'by ',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];
const routeObj: { name: Language; routes: RouteList }[] = [
    {
        name: 'nb',
        routes: routesNO,
    },
    {
        name: 'en',
        routes: routesEN,
    },
];

const QuestionInfoPage: React.FC<Props> = ({
    language,
}: Props): React.ReactElement => {
    const navigate = useNavigate();
    const location = useTypedLocation<LocationState>();

    const {
        addToConversationText,
        addToConversationToastText,
        addToFavouritesLabel,
        createConversationLabel,
        deleteQuestionButtonText,
        deleteQuestionCancelText,
        deleteQuestionTitle,
        removeFromFavouritesLabel,
        seoTitle,
        shareQuestionText,
        toFrontPage,
        byText,
    } = returnCopyForLanguage(language, copies);
    const hash = location.hash.substring(1);
    const routes = returnRoutesForLanguage(language, routeObj);
    const [isLoading, setIsLoading] = useState(true);
    const [isFavourite, setIsFavourite] = useState<boolean>(false);
    const [question, setQuestion] = useState<Question | null>(null);
    const favouriteQuestions = useSelector(favouriteQuestionsSelector);
    const parentPage = useMemo<string>(() => location.state?.parentPage, []);

    const searchState = {
        searchString: location.state?.searchString,
        viewAllSearchConvos: location.state?.viewAllSearchConvos,
        conversationSearchResult: location.state?.conversationSearchResult,
        questionSearchResult: location.state?.questionSearchResult,
    };

    const [
        showToggleFavouriteConfirmation,
        setShowToggleFavouriteConfirmation,
    ] = useState<boolean>(false);
    const [questionCanBeShared, setQuestionCanBeShared] =
        useState<boolean>(false);
    const [finishedCheckingSharing, setFinishedCheckingSharing] =
        useState<boolean>(false);
    const [isAddingQuestionToConversation, setIsAddingQuestionToConversation] =
        useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const navigator: any = window.navigator;
    const myConversations = useSelector(myConversationsSelector);
    const decorator = new Decorator();

    const createConversationObject: ConversationWithCategory = {
        category: {
            slug: '',
            name: '',
        },
        id: 0,
        name: createConversationLabel,
        count: 0,
        parent: 0,
        color_option: 0,
        slug: 'new-conversation',
        description: '',
        taxonomy: '',
    };

    const convos = myConversations && [...myConversations];

    const myConversationsToDisplay = convos?.map((c) => {
        return {
            ...c,
            category: { ...c.category, name: byText + c.category.name },
        };
    });
    myConversationsToDisplay &&
        myConversationsToDisplay.unshift(createConversationObject);

    const dispatch = useDispatch();

    const { id: questionId } = useSearchQuery();

    useEffect(() => {
        setIsLoading(true);
        if (myConversations === null) {
            conversationClient.getMyConversations().then((response) => {
                dispatch(setMyConversations(response.reverse()));
            });
        }
        // eslint-disable-next-line
    }, []);

    const onExit = () => {
        if (isAddingQuestionToConversation)
            setIsAddingQuestionToConversation(false);
        else {
            if (!questionId) {
                if (location.state?.searchString?.length > 1) {
                    navigate(routes.search, {
                        state: {
                            searchString: location.state?.searchString,
                            viewAllSearchQuestions:
                                location.state.viewAllSearchQuestions,
                            conversationSearchResult:
                                location.state.conversationSearchResult,
                            questionSearchResult:
                                location.state.questionSearchResult,
                        },
                    });
                } else navigate(-1);
            } else
                navigate({
                    pathname: routes.index,
                });
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        if (questionId === null) {
            navigate(routes.error);
            return;
        }
        let localQuestionId: string | string[] | number = questionId;
        // questionId is set to undefined when navigating to the more actions page pressing the three dots
        // It is unknown why it is set to undefined
        if (questionId === undefined) {
            localQuestionId = location.state?.question.id;
            conversationClient
                .questionCanBeShared(localQuestionId)
                .then((canBeShared) => {
                    setQuestionCanBeShared(canBeShared);
                    setFinishedCheckingSharing(true);
                });
        }
        if (question === null) {
            conversationClient
                .getQuestion(localQuestionId.toString())
                .then((response) => {
                    setQuestion(response);
                    setIsLoading(false);
                })
                .catch(() => {
                    console.error('Could not fetch question');
                    navigate(routes.error);
                });
        }
    }, [questionId]);

    useEffect(() => {
        async function fetchFavourites(): Promise<void> {
            if (hash && hash.length) {
                const anchorElement = document.getElementById(hash);
                if (anchorElement) anchorElement.scrollIntoView();
            }

            if (!favouriteQuestions) {
                Promise.all([
                    conversationClient.getMyFavouriteQuestions(),
                ]).then((response) => {
                    dispatch(setFavouriteQuestions(response[0].reverse()));

                    //Check using slug, as there could be duplicates
                    if (response.length > 0) {
                        setIsLoading(false);
                    }
                });
            } else {
                question &&
                favouriteQuestions.map((fq) => fq.slug).includes(question.slug)
                    ? setIsFavourite(true)
                    : setIsFavourite(false);
                setIsLoading(false);
            }
        }

        fetchFavourites();
    }, [question, favouriteQuestions, dispatch, hash]);

    if (isLoading || !question || (!finishedCheckingSharing && !questionId)) {
        return <Loading language={language} />;
    }

    //Canvas for sharing meta data
    const canvas = createCanvas(1200, 627);
    const ctx = canvas.getContext('2d');

    //Text wrap for canvas text
    function getLines(text: string, maxWidth: number) {
        const words = text.split(' ');
        const lines = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = ctx.measureText(currentLine + ' ' + word).width;
            if (width < maxWidth) {
                currentLine += ' ' + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);
        return lines;
    }

    const bgImagePath =
        'data:image/jpeg;base64,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';

    const bgImageLoaded = (bgImage: Image) => {
        ctx.drawImage(bgImage, 0, 0, 1200, 627);
        ctx.beginPath();
        ctx.font = '600 ' + 56 + 'px Source Sans Pro';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#2b4a64';

        for (let i = 0; i < getLines(question.text, 800).length; i++) {
            const line = getLines(question.text, 800)[i];
            ctx.fillText(line, 600, 260 + 80 * i, 800);
        }
        ctx.stroke();
    };
    loadImage(bgImagePath).then(bgImageLoaded);

    //TO test image for develop
    // const download = () => {
    //     const url = canvas.toDataURL('image/png');
    //     const link = document.createElement('a');
    //     link.download = 'filename.png';
    //     link.href = url;
    //     link.click();
    // };

    const saveImageToQuestion = async () => {
        const url = canvas.toDataURL('image/png');

        sharingClient.setQuestionImage({
            question_id: question.id,
            language: language,
            image: url,
            force_replace: true,
        });
    };

    const addQuestionToFavourites = async (question: Question) => {
        setIsFavourite(true);
        if (favouriteQuestions) {
            const questionsCopy = favouriteQuestions.slice();
            questionsCopy.unshift(question);
            dispatch(setFavouriteQuestions(questionsCopy));
        } else {
            const newQuestionList: Question[] = [question];
            dispatch(setFavouriteQuestions(newQuestionList));
        }

        try {
            const questionToAdd: FavouriteQuestion = {
                question_id: question!.id,
            };

            await conversationClient.addFavouriteQuestion(questionToAdd);
        } catch (err) {
            console.error('Could not add question to favourites', err);
        }
    };

    const toggleFavouriteQuestion = () => {
        if (question) {
            if (isFavourite) {
                setShowToggleFavouriteConfirmation(true);
            } else {
                addQuestionToFavourites(question);
            }
        }
    };

    const getHostToShare = () => {
        const localhost =
            'http://localhost:3000' + routes.questionInfoPage + '?id=';
        const develop =
            'https://develop.app.fuelbox.no' + routes.questionInfoPage + '?id=';
        const test =
            'https://test.app.fuelbox.no' + routes.questionInfoPage + '?id=';
        const staging =
            'https://staging.fuelbox.no' + routes.questionInfoPage + '?id=';
        const prod =
            'https://app.fuelbox.no' + routes.questionInfoPage + '?id=';

        const url = window.location.href;
        let host = '';
        if (IS_IOS_APP || IS_ANDROID_APP) {
            host += prod;
        } else if (url.indexOf('localhost:3000') !== -1) {
            return localhost;
        } else if (url.indexOf('develop.app') !== -1) {
            host += develop;
        } else if (url.indexOf('test.app') !== -1) {
            host += test;
        } else if (url.indexOf('staging.app') !== -1) {
            host += staging;
        } else {
            host += prod;
        }

        return host;
    };

    const shareQuestion = async () => {
        const urlToShare = getHostToShare() + question?.id;

        saveImageToQuestion().then(() =>
            navigator.share({
                title: question?.text,
                url: urlToShare,
            })
        );
    };

    const removeFavouriteQuestion = async (question: Question | null) => {
        setShowToggleFavouriteConfirmation(false);
        setIsFavourite(false);
        if (question) {
            if (favouriteQuestions) {
                const questionsCopy = favouriteQuestions.slice();
                questionsCopy.splice(questionsCopy.indexOf(question), 1);
                dispatch(setFavouriteQuestions(questionsCopy));
            }
            try {
                await conversationClient.removeFavouriteQuestion({
                    question_id: question.id,
                });
            } catch (err) {
                console.error('Could not remove question from favourites', err);
            }
        }
    };

    const onConversationSelect = (conversation: ConversationWithCategory) => {
        dispatch(
            setToastMessage(
                addToConversationToastText +
                    '\u00AB' +
                    conversation.name +
                    '\u00BB'
            )
        );
        if (conversation.id === 0) {
            navigate(routes.createConversation, {
                state: {
                    questionToAdd: location.state.question,
                    toastMessageText: addToConversationToastText,
                },
            });
        } else {
            conversationClient
                .addQuestionsToMyConversation(conversation.id, [question.id])
                .then(() => {
                    conversationClient.getMyConversations().then((response) => {
                        dispatch(setMyConversations(response.reverse()));
                    });
                });

            dispatch(
                setToastMessage(
                    addToConversationToastText +
                        '\u00AB' +
                        conversation.name +
                        '\u00BB'
                )
            );

            switch (parentPage) {
                case deckParentPages.premiumHome:
                    navigate(routes.index);
                    break;
                case deckParentPages.myLibrary:
                    navigate(routes.myLibrary);
                    break;
                case deckParentPages.search:
                    navigate(routes.search, {
                        state: searchState,
                    });
                    break;
                case deckParentPages.premiumBrowse:
                    const parentBrowsePath = `/${language}/${parentPage}`;
                    navigate(parentBrowsePath);
                    break;
                case deckParentPages.likedQuestionList:
                    navigate(routes.favouriteQuestionsList);
                    break;
                default:
                    navigate(routes.index);
                    break;
            }
        }
    };

    if (isAddingQuestionToConversation)
        return (
            <div className={styles.AddQuestionToConversationContainer}>
                <SEO title={seoTitle} />
                <div className={styles.bg} />
                <section
                    className={styles.BackButtonContainer}
                    id='BackButtonContainer'
                >
                    <BackButton
                        language={language}
                        className={styles.backButton}
                        clickHandler={onExit}
                    />
                </section>
                <div className={styles.headerContainer}>
                    <h2 className={styles.title}>{addToConversationText}</h2>
                </div>

                <section className={styles.ConversationCardsContainer}>
                    <section className={styles.ConversationCardsInnerContainer}>
                        {myConversationsToDisplay?.map((c) => {
                            if (c) {
                                return (
                                    <ConversationCard
                                        key={c.id}
                                        conversation={c}
                                        className={classNames(
                                            styles.ConversationCard,
                                            c.color_option === 1 &&
                                                'userCreatedConversation1',
                                            c.color_option === 2 &&
                                                'userCreatedConversation2',
                                            c.color_option === 3 &&
                                                'userCreatedConversation3'
                                        )}
                                        onClick={(): void =>
                                            onConversationSelect(c)
                                        }
                                        category={{
                                            slug: c.category.slug,
                                            name: c.category.name,
                                        }}
                                        decorator={decorator}
                                    />
                                );
                            }
                        })}
                    </section>
                </section>
            </div>
        );

    return (
        <div className={styles.QuestionInfoPageContainer}>
            <SEO title={seoTitle} />
            <div className={styles.bg} />
            <section
                className={styles.BackButtonContainer}
                id='BackButtonContainer'
            >
                <BackButton
                    language={language}
                    className={styles.backButton}
                    clickHandler={onExit}
                />
            </section>

            {questionId && (
                <div>
                    <button
                        className={styles.favouriteHeart}
                        onClick={() => {
                            toggleFavouriteQuestion();
                        }}
                    >
                        <div
                            className={
                                isFavourite
                                    ? styles.redHeartIcon
                                    : styles.redHeartIconOutline
                            }
                        ></div>
                    </button>
                    <button
                        className={styles.moreVert}
                        onClick={() => {
                            navigate(routes.questionInfoPage, {
                                state: { question },
                            });
                        }}
                    >
                        <MoreVertIcon />
                    </button>
                </div>
            )}

            <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                my='auto'
            >
                <section
                    className={
                        questionId
                            ? classnames(
                                  styles.QuestionSection,
                                  styles.QuestionSectionShared
                              )
                            : styles.QuestionSection
                    }
                >
                    {question?.text}
                </section>

                <Stack
                    spacing={1}
                    pt={10}
                    pb={(theme) =>
                        `calc(env(safe-area-inset-bottom) + ${theme.spacing(
                            4
                        )})`
                    }
                    alignItems='flex-start'
                >
                    {questionId ? (
                        <Button to={routes.index}>
                            <span>{toFrontPage}</span>
                        </Button>
                    ) : (
                        <Fragment>
                            {isFavourite ? (
                                <Button
                                    onClick={toggleFavouriteQuestion}
                                    appearance='text'
                                    leftIcon='favorite'
                                    // LeftIconProps={{ color: 'error' }}
                                >
                                    {removeFromFavouritesLabel}
                                </Button>
                            ) : (
                                <Button
                                    onClick={toggleFavouriteQuestion}
                                    appearance='text'
                                    leftIcon='favorite_border'
                                >
                                    {addToFavouritesLabel}
                                </Button>
                            )}

                            <Button
                                onClick={() =>
                                    setIsAddingQuestionToConversation(true)
                                }
                                appearance='text'
                                leftIcon='add'
                            >
                                {addToConversationText}
                            </Button>

                            {questionCanBeShared && navigator.canShare && (
                                <Button
                                    onClick={shareQuestion}
                                    appearance='text'
                                    leftIcon='share'
                                >
                                    {shareQuestionText}
                                </Button>
                            )}
                        </Fragment>
                    )}
                </Stack>

                {showToggleFavouriteConfirmation && (
                    <ToggleConfirmation
                        title={deleteQuestionTitle}
                        description={question.text}
                        cancelLabel={deleteQuestionCancelText}
                        buttonLabel={deleteQuestionButtonText}
                        onSubmit={() => removeFavouriteQuestion(question)}
                        onCancel={() =>
                            setShowToggleFavouriteConfirmation(false)
                        }
                    />
                )}
            </Box>
        </div>
    );
};

export default QuestionInfoPage;
